import {Injectable, signal, WritableSignal, effect, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {Observable, Subject, forkJoin, timer, of} from 'rxjs';
import {switchMap, tap, map, catchError, finalize} from 'rxjs/operators';

export interface LoadResult<T, P = void> {
  hasAttemptedLoad: WritableSignal<boolean>;
  isLoading: WritableSignal<boolean>;
  error: WritableSignal<string | null>;
  data: WritableSignal<T | null>;
  trigger: (params: P) => void;
}

type Response<R> = {resultData: R; error: null} | {resultData: null; error: string} | {resultData: null; error: null};

@Injectable({providedIn: 'root'})
export class HttpLoaderService {

  createLoader<
    R extends Record<string, any>,
    P extends {[K in keyof R]?: any}
  >(
    requestFactories: {[K in keyof R]: (params: P[K]) => Observable<R[K]>},
    minDurationMs: number = 500
  ): LoadResult<R, P> {
    const hasAttemptedLoad: WritableSignal<boolean> = signal(false);
    const isLoading: WritableSignal<boolean> = signal(false);
    const error: WritableSignal<string | null> = signal<string | null>(null);
    const data: WritableSignal<R | null> = signal<R | null>(null);

    const loadTrigger = new Subject<P>();

    const apiResponse: Signal<Response<R>> = toSignal(
      loadTrigger.pipe(
        tap((): void => {
          hasAttemptedLoad.set(true);
          isLoading.set(true);
          error.set(null);
          data.set(null);
        }),
        switchMap((paramsFromTrigger: P) => {
          const observablesToJoin: {[K in keyof R]?: Observable<any>} = {};
          (Object.keys(requestFactories) as Array<keyof R>).forEach((key: keyof R): void => {
            observablesToJoin[key] = requestFactories[key](paramsFromTrigger[key]);
          });
          if (minDurationMs > 0) {
            (observablesToJoin as Record<string, Observable<any>>)['_timer'] = timer(minDurationMs);
          }

          return forkJoin(observablesToJoin as Record<string, Observable<any>>).pipe(
            map((results): Response<R> => {
              // Excluye el resultado del timer
              const {_timer, ...actualResults} = results;
              return {resultData: actualResults as R, error: null};
            }),
            catchError((err): Observable<Response<R>> => {
              console.error('Error en parallel load service:', err);
              return of({resultData: null, error: 'Error al cargar datos.'});
            }),
            finalize(() => {
              isLoading.set(false);
            })
          );
        })
      ),
      {initialValue: {resultData: null, error: null}}
    );

    // Effect para actualizar los signals de datos y error del servicio
    // (o el componente podria leer apiResponse directamente si se expone)
    effect(() => {
      const res: Response<R> = apiResponse();
      // No es necesario chequear `if (res)` porque toSignal con initialValue siempre emitira
      if (res.error) {
        error.set(res.error);
        data.set(null);
      } else if (res.resultData) {
        error.set(null);
        data.set(res.resultData as R);
      }
    });

    return {
      hasAttemptedLoad,
      isLoading,
      error,
      data,
      trigger: (params: P): void => loadTrigger.next(params),
    };
  }
}
